import { openCommandIdOf } from '../commands/semantics.ts'
import { AthenaError, SessionExpiredError } from './client.ts'

/**
 * Tradução de `code` para frase.
 *
 * A interface decide o que dizer a partir do **código**, nunca do `detail` — o
 * `detail` é texto do servidor, muda de redação e às vezes carrega detalhe que
 * só faz sentido no log. Código desconhecido cai no `detail`, que ainda é
 * melhor do que "erro inesperado".
 */
const MESSAGE: Record<string, string> = {
  // Empresas
  company_code_required: 'Informe o código da empresa.',
  company_code_too_long: 'O código passa de 64 caracteres.',
  company_name_required: 'Informe a razão social.',
  company_name_too_long: 'A razão social passa de 200 caracteres.',
  company_code_taken: 'Já existe uma empresa com esse código.',
  company_not_found: 'Empresa não encontrada.',
  company_has_live_product_keys:
    'A empresa ainda tem licenças não revogadas. Revogue todas antes de remover.',
  company_inactive: 'A empresa está desativada.',

  // Máquinas
  fingerprint_required: 'Informe o fingerprint da máquina.',
  fingerprint_too_long: 'O fingerprint passa de 256 caracteres.',
  fingerprint_taken: 'Já existe uma máquina com esse fingerprint.',
  machine_not_found: 'Máquina não encontrada.',
  machine_has_live_product_keys:
    'A máquina ainda tem licenças não revogadas. Revogue todas antes de remover.',

  // Licenças
  company_id_required: 'Escolha a empresa dona da licença.',
  machine_id_invalid: 'A máquina informada é inválida.',
  product_key_value_invalid: 'Esse texto não tem o formato de uma chave (ATHN-…).',
  product_key_not_found: 'Licença não encontrada.',
  machine_already_licensed: 'Essa máquina já tem outra licença ativa.',
  product_key_transition_invalid: 'Essa transição de status não é permitida.',
  product_key_machine_required: 'Vincule uma máquina antes de ativar a licença.',
  product_key_active: 'Não dá para desvincular a máquina de uma licença ativa. Suspenda antes.',
  product_key_revoked: 'A licença está revogada — não aceita mais vínculo.',
  product_key_status_invalid: 'Status de licença desconhecido.',

  // Agentes
  agent_not_found: 'Agente não encontrado.',

  // Logs (§7.1) — os quatro de escopo são prevenidos na interface (§5.1); o code
  // aqui é a rede de segurança, para o caso de o filtro escapar mesmo assim.
  logs_scope_required_for_time_range: 'Escolha uma empresa ou um agente antes de filtrar por período.',
  logs_scope_required_for_event_type: 'Escolha uma empresa ou um agente antes de filtrar por tipo.',
  logs_scope_required_for_event_action: 'Escolha uma empresa ou um agente antes de filtrar por ação.',
  logs_scope_required_for_user: 'Escolha uma empresa ou um agente antes de filtrar por usuário.',
  logs_endpoint_required_for_source: 'Escolha um agente antes de filtrar por canal.',
  logs_endpoint_required_for_native_id: 'Escolha um agente antes de filtrar por identificador de origem.',
  logs_scope_required_for_event_module: 'Escolha uma empresa ou um agente antes de filtrar por módulo.',
  logs_scope_required_for_process_name: 'Escolha uma empresa ou um agente antes de filtrar por nome de processo.',
  logs_scope_required_for_service_name: 'Escolha uma empresa ou um agente antes de filtrar por serviço.',
  logs_scope_required_for_file_extension: 'Escolha uma empresa ou um agente antes de filtrar por extensão de arquivo.',
  logs_event_module_invalid: 'Módulo fora do vocabulário conhecido.',
  logs_event_type_invalid: 'Tipo de evento vazio ou acima de 100 caracteres.',
  logs_event_action_invalid: 'Ação vazia ou acima de 100 caracteres.',
  logs_related_ip_invalid: 'O IP informado é inválido (IPv4 ou IPv6).',
  logs_time_range_inverted: 'O início do período está depois do fim.',
  logs_cursor_invalid: 'A paginação expirou. Recarregue a listagem do começo.',
  detections_response_action_invalid:
    'Ação de resposta inválida: use só letras minúsculas, números e sublinhado (até 64).',
  detections_response_status_invalid:
    'Desfecho de resposta inválido: use só letras minúsculas, números e sublinhado (até 64).',
  logs_endpoint_required: 'Falta o agente para resolver este evento.',
  logs_event_time_required: 'Falta o horário para resolver este evento.',
  log_not_found: 'Detalhe não disponível: o evento não existe ou saiu por retenção.',
  process_not_found: 'Nenhum evento deste processo: o id não existe ou saiu por retenção.',
  response_shape_invalid: 'O servidor respondeu num formato que o console não reconhece.',

  // Comandos (§7.2). endpoint_has_no_agent, agent_not_active e tenant_inactive são
  // três coisas diferentes (§7.2): cada uma tem a sua frase abaixo, nunca uma genérica.
  agent_command_kind_unknown: 'Tipo de comando inválido.',
  agent_command_params_not_accepted: 'Este comando não aceita argumentos.',
  agent_command_params_required:
    'Faltam argumentos para este comando (o IP, a rede, o challenge ou a exceção).',
  agent_command_params_invalid: 'Os argumentos são inválidos ou o artefato é desconhecido.',
  agent_command_params_too_large: 'Os argumentos passam de 4 KiB.',
  agent_command_reason_required:
    'A justificativa é obrigatória (na permissão de IP, a nota).',
  agent_command_reason_too_long: 'A justificativa passa de 512 caracteres.',
  agent_command_ttl_out_of_range: 'A validade do comando precisa ficar entre 1 e 60 minutos.',
  agent_command_requires_admin: 'Este comando exige o papel de administrador.',
  // 404 também é o que o operador recebe quando a PERMISSÃO é de outra empresa:
  // de propósito, para a concessão não servir de sonda.
  endpoint_not_found: 'Máquina não encontrada.',
  agent_command_not_found: 'Comando não encontrado.',
  dispatch_unavailable: 'Não há como entregar o comando. Reconheça a intenção para registrá-lo.',
  agent_command_already_open: 'Já existe um comando aberto para este alvo nesta máquina.',
  // Regime de isolamento (README do Athena §4.9)
  agent_command_isolation_mode_invalid: 'Regime de isolamento inválido: use Automático, Só recomendar ou Desligado.',
  agent_artifact_not_found: 'Artefato não encontrado.',
  agent_command_not_cancellable: 'Este comando não pode mais ser cancelado.',

  // Firewall de IP do host (HOST_IP_FIREWALL.md §D)
  agent_command_requires_ip_permission:
    'Só administradores podem gerenciar IPs.',
  agent_command_ip_invalid: 'Não é um IPv4 ou IPv6 válido (sem prefixo, sem zona).',
  agent_command_ip_not_blockable:
    'Este endereço não pode ser bloqueado (loopback, não especificado, multicast ou broadcast).',
  agent_command_ip_protected:
    'Endereço do Athena: não pode ser bloqueado.',
  agent_command_cidr_invalid: 'Não é um IP nem uma rede (CIDR) válida.',
  agent_command_cidr_too_wide: 'Rede larga demais: o mínimo é /8 no IPv4 e /32 no IPv6.',
  agent_command_ip_allowlisted:
    'Este IP está na allowlist da máquina. Retire a permissão antes de bloqueá-lo.',
  endpoint_has_no_agent: 'A máquina não tem agente vinculado.',
  agent_not_active: 'O agente desta máquina não está ativo.',
  tenant_inactive: 'A empresa dona desta máquina está inativa.',
  agent_command_fleet_too_large: 'A empresa tem máquinas demais para um comando de frota (teto de 2.000).',

  // Web Defense (HERMES_WEB_DEFENSE.md §9)
  agent_command_challenge_id_invalid: 'O id do challenge é inválido.',
  agent_command_exception_id_invalid: 'O id da exceção é inválido.',
  agent_command_decision_invalid: 'A decisão precisa ser liberar ou bloquear.',
  agent_command_scope_invalid: 'O escopo precisa ser o IP ou a regra na rota.',
  web_challenge_state_invalid: 'Filtro de estado inválido: use pendentes ou todos.',
  web_challenge_ip_invalid: 'O IP do filtro é inválido (IPv4 ou IPv6).',
  web_challenge_not_found: 'O Athena não recebeu este challenge desta máquina.',
  web_exceptions_not_listed: 'As exceções desta máquina nunca foram listadas.',

  // Análise por IA
  ai_findings_status_invalid: 'Filtro de status inválido.',
  ai_findings_verdict_invalid: 'Filtro de veredito inválido.',
  ai_findings_time_range_inverted: 'O início do período é depois do fim.',
  ai_findings_cursor_invalid: 'A página pedida não vale mais. Recarregue a lista.',
  ai_findings_not_found: 'Achado não encontrado.',
  ai_findings_decision_invalid: 'A decisão precisa ser confirmar ou descartar.',
  logs_not_found: 'Evento não encontrado.',

  // Concessões de IP (admin)
  ip_management_grant_exists: 'Este usuário já tem essa concessão.',
  ip_management_grant_not_found: 'Concessão não encontrada.',

  // Autenticação
  invalid_credentials: 'Usuário ou senha inválidos.',
  refresh_token_missing: 'Sessão encerrada. Entre novamente.',
  refresh_token_invalid: 'Sessão encerrada. Entre novamente.',
  user_inactive: 'Esta conta foi desativada.',
  current_password_invalid: 'A senha atual não confere.',
  password_unchanged: 'A nova senha precisa ser diferente da atual.',
  email_not_configured: 'Envio de e-mail não configurado. Nada foi alterado.',
  email_send_failed: 'Não foi possível enviar o e-mail com as credenciais. Nada foi alterado; tente de novo.',
  session_not_found: 'Sessão não encontrada.',

  // Usuários
  username_required: 'Informe o login.',
  username_invalid: 'O login aceita apenas letras minúsculas, números, ponto, hífen e sublinhado.',
  username_too_long: 'O login passa de 64 caracteres.',
  email_required: 'Informe o e-mail.',
  email_invalid: 'E-mail inválido.',
  email_too_long: 'O e-mail passa de 254 caracteres.',
  display_name_too_long: 'O nome de exibição passa de 128 caracteres.',
  password_required: 'Informe a senha.',
  password_too_short: 'A senha precisa de ao menos 12 caracteres.',
  password_too_long: 'A senha passa de 256 caracteres.',
  roles_required: 'Escolha ao menos um papel.',
  role_unknown: 'Papel desconhecido.',
  username_taken: 'Já existe um usuário com esse login.',
  email_taken: 'Já existe um usuário com esse e-mail.',
  user_not_found: 'Usuário não encontrado.',
  cannot_deactivate_self: 'Você não pode desativar a própria conta.',
  cannot_drop_own_admin_role: 'Você não pode remover o próprio papel de administrador.',
  cannot_delete_self: 'Você não pode remover a própria conta.',
  roles_scope_mixed: 'Papéis da Black Ghost e de cliente não podem ser misturados no mesmo usuário.',
  company_required_for_tenant_roles: 'Usuário de cliente precisa de uma empresa.',
  company_not_allowed_for_platform_roles: 'Usuário da Black Ghost não pode ter empresa.',
  ip_grant_requires_platform_user:
    'Permissão de IP só existe para usuários da Black Ghost, não para usuários de cliente.',

  // Estatísticas
  stats_time_range_too_wide: 'O período passa de 31 dias. Escolha uma janela menor.',
  stats_time_range_inverted: 'O início do período está depois do fim.',
  tenant_id_invalid: 'A empresa informada é inválida.',
}

/**
 * 403 chega sem `code`: é escopo ou papel errado, nos dois escopos (um cliente
 * na área da Black Ghost, a Black Ghost na do cliente, ou falta de papel dentro
 * do escopo). Renovar a sessão não resolve, então a frase não sugere retry.
 */
/**
 * 401 `company_inactive` no refresh: a empresa da conta foi desativada ou
 * removida e todas as sessões caíram (§1.5). A tela de login mostra esta frase
 * no lugar de "sessão expirada", que mandaria o usuário tentar de novo à toa.
 */
export const COMPANY_INACTIVE_SESSION_MESSAGE = 'A empresa desta conta foi desativada.'

export const FORBIDDEN_MESSAGE =
  'Sem permissão nesta área.'

/** Campo do formulário que cada código de validação acusa. */
export const FIELD_OF_CODE: Record<string, string> = {
  company_code_required: 'companyCode',
  company_code_too_long: 'companyCode',
  company_code_taken: 'companyCode',
  company_name_required: 'name',
  company_name_too_long: 'name',
  fingerprint_required: 'fingerprint',
  fingerprint_too_long: 'fingerprint',
  fingerprint_taken: 'fingerprint',
  company_id_required: 'companyId',
  machine_id_invalid: 'machineId',
  username_required: 'username',
  username_invalid: 'username',
  username_too_long: 'username',
  username_taken: 'username',
  email_required: 'email',
  email_invalid: 'email',
  email_too_long: 'email',
  email_taken: 'email',
  display_name_too_long: 'displayName',
  password_required: 'password',
  password_too_short: 'password',
  password_too_long: 'password',
  roles_required: 'roles',
  role_unknown: 'roles',
  current_password_invalid: 'currentPassword',
  password_unchanged: 'password',
  roles_scope_mixed: 'roles',
  company_required_for_tenant_roles: 'companyId',
  company_not_allowed_for_platform_roles: 'roles',
}

/**
 * Isolar e desbloquear dividem a chave de deduplicação: o `409` ao isolar pode
 * ser um DESBLOQUEIO aberto. A frase genérica diria "isolamento" e mentiria.
 */
export const ISOLATION_ALREADY_OPEN_MESSAGE =
  'Já há um isolamento ou um desbloqueio aberto para esta máquina.'

/** Frase para o operador, a partir de qualquer coisa que tenha sido lançada. */
export function describeError(error: unknown): string {
  if (error instanceof SessionExpiredError) return 'Sessão expirada. Entre novamente.'

  if (error instanceof AthenaError) {
    const known = MESSAGE[error.code]
    if (known !== undefined) {
      // Já há comando aberto para o alvo: o id é o que leva o operador até ele.
      if (error.code === 'agent_command_already_open') {
        const id = openCommandIdOf(error.problem.detail)
        if (id !== null) return `${known} Comando ${id}.`
      }
      return known
    }

    // 400 do binding do framework: a mensagem útil está em `errors`.
    const binding = Object.values(error.fieldErrors).flat()[0]
    if (binding !== undefined) return binding

    if (error.status === 403) return FORBIDDEN_MESSAGE

    // Objeto aberto por link (log, processo, máquina, usuário) que não está ao
    // alcance desta conta. A frase não sugere que ele exista em outra empresa.
    if (error.status === 404) return 'Não encontrado.'

    // Falha nossa: o traceId é o que o suporte pede para achar a linha do log.
    // Sem ele o operador só consegue descrever o sintoma.
    if (error.status >= 500) {
      return error.traceId === null
        ? 'Falha no servidor.'
        : `Falha no servidor. Informe ao suporte o traceId ${error.traceId}`
    }

    return error.problem.detail ?? error.problem.title ?? 'Falha na requisição.'
  }

  // Rede caída, proxy fora, DNS: o fetch rejeita antes de haver resposta.
  if (error instanceof TypeError) return 'Não foi possível falar com o servidor.'

  return 'Falha inesperada.'
}

/**
 * Frase para um `error_code` que NÃO chegou como exceção — o item de uma
 * operação de frota. Sem tradução, cai na mensagem do servidor.
 */
export function describeCode(code: string | null, fallback: string | null): string {
  if (code !== null) {
    const known = MESSAGE[code]
    if (known !== undefined) return known
  }
  return fallback ?? code ?? 'Falha sem detalhe.'
}

/**
 * 403 `agent_command_requires_ip_permission`: terminal — repetir devolve o mesmo
 * 403. A tela desabilita o envio em vez de oferecer nova tentativa.
 */
export function isIpPermissionDenied(error: unknown): boolean {
  return error instanceof AthenaError && error.code === 'agent_command_requires_ip_permission'
}

/** Campo do formulário a marcar, se o erro apontar para um. */
export function fieldOfError(error: unknown): string | null {
  if (!(error instanceof AthenaError)) return null

  const direct = FIELD_OF_CODE[error.code]
  if (direct !== undefined) return direct

  const first = Object.keys(error.fieldErrors)[0]
  if (first === undefined) return null

  // O binding usa PascalCase ("CompanyCode"); o formulário usa camelCase.
  return first.charAt(0).toLowerCase() + first.slice(1)
}
